import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TangramIcon } from "@/components/TangramIcon";
import { Loader2, Download, Sparkles, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1758979658225-c833408a2e0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwb2ZmaWNlJTIwbWFuYWdlcnxlbnwwfHx8fDE3ODIzNTY0NzZ8MA&ixlib=rb-4.1.0&q=85";
const FOLLETO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/documents/folleto/file`;

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  const [acquireOpen, setAcquireOpen] = useState(false);
  const [folletoAvailable, setFolletoAvailable] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", phone: "", company: "" });
  const [leadBusy, setLeadBusy] = useState(false);

  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [forgotMsg, setForgotMsg] = useState("");
  const [resetLink, setResetLink] = useState("");

  useEffect(() => {
    api.get("/public/documents/folleto").then(({ data }) => setFolletoAvailable(!!data.exists)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      await login(email, password);
      toast.success("Bienvenido de nuevo");
      navigate("/");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const submitLead = async (kind) => {
    if (!lead.name.trim() || !lead.email.trim()) { toast.error("Nombre y correo son obligatorios"); return; }
    if (!lead.company.trim()) { toast.error("Indica tu empresa"); return; }
    setLeadBusy(true);
    try {
      const { data } = await api.post("/leads", { ...lead, kind });
      toast.success(data.message);
      setLead({ name: "", email: "", phone: "", company: "" });
      setAcquireOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo enviar la solicitud");
    } finally {
      setLeadBusy(false);
    }
  };

  const openForgot = () => {
    setForgotEmail(email);
    setForgotMsg("");
    setResetLink("");
    setForgotOpen(true);
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail.trim()) { toast.error("Ingresa tu correo"); return; }
    setForgotBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotMsg(data.message);
      setResetLink(data.reset_token ? `${window.location.origin}/reset-password?token=${data.reset_token}` : "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo procesar la solicitud");
    } finally {
      setForgotBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#0A2540]/85" />
        <div className="relative h-full flex flex-col p-12 text-white">
          <div className="flex items-center gap-2.5">
            <TangramIcon size={24} outline />
            <span className="font-display text-xl font-extrabold tracking-tight">TANGRAM</span>
          </div>
          <div className="mt-auto">
            <h1 className="font-display text-4xl xl:text-5xl font-extrabold leading-[0.95] tracking-tighter">
              Cada activo.<br/>Cada proyecto.<br/>Una sola fuente de verdad.
            </h1>
            <p className="mt-6 text-white/70 max-w-md text-sm leading-relaxed" data-testid="login-left-copy">
              Da seguimiento a todo lo que se compra en los proyectos. Encuentra inventario sobrante antes de volver a comprar. Hecho para empresas que no quieren pagar dos veces por la misma silla.
            </p>
            <div className="mt-12 grid grid-cols-3 gap-6 text-white/70 text-[11px] uppercase tracking-[0.2em]">
              <div><div className="text-white text-2xl font-display font-extrabold">100%</div>Visibilidad</div>
              <div><div className="text-white text-2xl font-display font-extrabold">0</div>Duplicados</div>
              <div><div className="text-white text-2xl font-display font-extrabold">MXN</div>Valor reutilizable</div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-12 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-2.5 text-[#0A2540]">
            <TangramIcon size={22} outline stroke="#0A2540" />
            <span className="font-display text-lg font-extrabold tracking-tight">TANGRAM</span>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#5C6A7D]">Iniciar sesión</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-[#0A2540] mt-1" data-testid="login-right-heading">
              Accede a TANGRAM
            </h2>
          </div>

          {err && (
            <div className="text-sm text-[#E11D48] bg-[#E11D48]/8 border border-[#E11D48]/20 px-3 py-2 rounded-sm" data-testid="login-error">
              {err}
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Correo electrónico</Label>
            <Input
              id="email" type="email" required
              value={email} onChange={(e) => setEmail(e.target.value)}
              className="rounded-sm border-[#E2E8F0] h-11"
              data-testid="login-email-input"
              placeholder="tu@empresa.com"
            />
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Contraseña</Label>
              <button type="button" onClick={openForgot} className="text-xs text-[#0052CC] font-semibold hover:underline" data-testid="forgot-password-link">
                ¿Olvidaste tu contraseña?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password" type={showPassword ? "text" : "password"} required
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm border-[#E2E8F0] h-11 pr-11"
                data-testid="login-password-input"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                title={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                className="absolute inset-y-0 right-2 flex items-center justify-center w-8 text-[#5C6A7D] hover:text-[#0A2540] transition-colors"
                data-testid="login-password-toggle"
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <Button
            type="submit" disabled={submitting}
            className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11 font-semibold tracking-wide"
            data-testid="login-submit-button"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : "Iniciar sesión"}
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-[#E2E8F0]" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#5C6A7D]">¿Aún no usas Tangram?</span>
            <div className="h-px flex-1 bg-[#E2E8F0]" />
          </div>

          <Button
            type="button"
            onClick={() => setAcquireOpen(true)}
            variant="outline"
            className="w-full border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 rounded-sm h-11 font-semibold tracking-wide"
            data-testid="acquire-tangram-button"
          >
            <Sparkles size={16} className="mr-2" /> Adquiere Tangram
          </Button>

          <p className="text-sm text-[#5C6A7D]">
            ¿Cuenta nueva? <Link to="/register" className="text-[#0052CC] font-semibold" data-testid="register-link">Crea una</Link>
          </p>
        </form>
      </div>

      {/* Adquiere Tangram — captación de leads */}
      <Dialog open={acquireOpen} onOpenChange={setAcquireOpen}>
        <DialogContent className="rounded-sm" data-testid="acquire-modal">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Adquiere Tangram</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C6A7D]">Déjanos tus datos y el equipo de Tangram te contactará. También puedes descargar el folleto.</p>

          {folletoAvailable && (
            <a
              href={FOLLETO_URL} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#0052CC] hover:underline"
              data-testid="folleto-download-link"
            >
              <Download size={16} /> Descargar folleto
            </a>
          )}

          <div className="space-y-3 mt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Nombre</Label>
              <Input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} className="rounded-sm h-11" data-testid="lead-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Correo</Label>
              <Input type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} className="rounded-sm h-11" data-testid="lead-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Número celular (opcional)</Label>
              <Input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="rounded-sm h-11" data-testid="lead-phone-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Empresa</Label>
              <Input value={lead.company} onChange={(e) => setLead({ ...lead, company: e.target.value })} className="rounded-sm h-11" data-testid="lead-company-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-2">
            <Button variant="outline" disabled={leadBusy} onClick={() => submitLead("informes")} className="rounded-sm border-[#0A2540] text-[#0A2540]" data-testid="request-info-button">
              Solicitar informes
            </Button>
            <Button disabled={leadBusy} onClick={() => submitLead("cuenta")} className="rounded-sm bg-[#0A2540] hover:bg-[#173B63] text-white" data-testid="request-account-button">
              Solicitar cuenta
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Olvidé mi contraseña — sin proveedor real: se genera y muestra el enlace */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="rounded-sm" data-testid="forgot-modal">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Recuperar contraseña</DialogTitle></DialogHeader>
          <form onSubmit={submitForgot} className="space-y-3">
            <p className="text-sm text-[#5C6A7D]">Ingresa tu correo y generaremos un enlace para restablecer tu contraseña. El enlace expira en 30 minutos.</p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C6A7D]">Correo</Label>
              <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} className="rounded-sm h-11" data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={forgotBusy} className="w-full bg-[#0A2540] hover:bg-[#173B63] text-white rounded-sm h-11" data-testid="forgot-submit-button">
              {forgotBusy ? <Loader2 className="animate-spin" size={16} /> : "Enviar enlace"}
            </Button>
          </form>

          {forgotMsg && (
            <div className="text-sm text-[#0A2540] bg-[#F1F5F9] border border-[#E2E8F0] rounded-sm p-3 space-y-2" data-testid="forgot-result">
              <p>{forgotMsg}</p>
              {resetLink && (
                <div className="space-y-2">
                  <p className="text-xs text-[#5C6A7D]">Como aún no hay servicio de correo, usa este enlace directamente:</p>
                  <a href={resetLink} className="block text-xs text-[#0052CC] break-all underline" data-testid="reset-link">{resetLink}</a>
                  <Button type="button" onClick={() => navigate(resetLink.replace(window.location.origin, ""))} className="w-full bg-[#0052CC] hover:bg-[#0041a3] text-white rounded-sm h-10" data-testid="open-reset-link-button">
                    Abrir enlace de recuperación
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
